import { Button, Center, Divider, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconFileTypeXls } from "@tabler/icons-react";
import { useAbility } from "configs/casl";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import {
  EXPORT_JOB_STATUS_COLOR,
  EXPORT_JOB_STATUS_LABEL,
  EXPORT_MODULE_LABEL,
} from "constants/common/exportJob";
import { FC, ReactNode } from "react";
import { formatDate } from "utils/helpers/format";
import { useExportJobDetail } from "utils/hooks/request/useExportJobs";

const InfoField: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">{label}</Text>
    <Text size="sm" fw={500} style={{ wordBreak: "break-all" }}>
      {value ?? "—"}
    </Text>
  </Stack>
);

interface ExportJobDetailModalProps {
  jobId: string;
  userMap: Record<string, string>;
}

const ExportJobDetailModal: FC<ExportJobDetailModalProps> = ({ jobId, userMap }) => {
  const { job, isLoading, error } = useExportJobDetail(jobId);
  const ability = useAbility();
  const canRead = ability.can("read", "export_job");

  if (isLoading) {
    return (
      <Center h={160}>
        <Loader size="md" />
      </Center>
    );
  }

  if (error || !job) {
    return (
      <Center h={120}>
        <Stack align="center" gap="xs">
          <IconAlertTriangle size={32} color="red" />
          <Text size="sm" c="dimmed">Không thể tải dữ liệu</Text>
        </Stack>
      </Center>
    );
  }

  const requestedByName = job.requestedById
    ? (userMap[job.requestedById] ?? job.requestedById)
    : "—";

  return (
    <Stack gap="md">
      <SimpleGrid cols={2} spacing="sm">
        <InfoField
          label="Module"
          value={EXPORT_MODULE_LABEL[job.module] ?? job.module ?? "—"}
        />
        <InfoField
          label="Trạng thái"
          value={
            <CustomBadge
              label={EXPORT_JOB_STATUS_LABEL[job.status] ?? job.status}
              color={EXPORT_JOB_STATUS_COLOR[job.status] ?? "gray"}
            />
          }
        />
        <InfoField label="Người xuất" value={requestedByName} />
        <InfoField label="Ngày tạo" value={formatDate(job.createdAt)} />
        <InfoField
          label="Từ ngày"
          value={job.fromDate ? formatDate(job.fromDate) : "—"}
        />
        <InfoField
          label="Đến ngày"
          value={job.toDate ? formatDate(job.toDate) : "—"}
        />
      </SimpleGrid>

      {job.errorMessage && (
        <>
          <Divider />
          <InfoField label="Lỗi" value={
            <Text size="sm" c="red" fw={500}>{job.errorMessage}</Text>
          } />
        </>
      )}

      <Divider />

      <Stack gap="xs">
        <Text size="xs" c="dimmed">Tải file</Text>
        {job.downloadUrl && canRead ? (
          <Button
            component="a"
            href={job.downloadUrl}
            download
            target="_blank"
            rel="noreferrer"
            leftSection={<IconFileTypeXls size={16} />}
            color="green"
            variant="light"
            size="sm"
            w="fit-content"
          >
            Tải file Excel
          </Button>
        ) : (
          <Text size="sm" c="dimmed">Chưa có file</Text>
        )}
      </Stack>
    </Stack>
  );
};

export default ExportJobDetailModal;
